// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const handleResponse = res => {
  const contentTypeHeader = res.headers.get('Content-Type');
  const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

  if (res.status >= 400) {
    return res.json().then(data => {
      let e = new Error();
      e = Object.assign(e, { status: res.status }, data);

      throw e;
    });
  }
  if (contentType === 'application/transit+json') {
    return res.text().then(deserialize);
  } else if (contentType === 'application/json') {
    return res.json();
  }
  return res.text();
};

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return window.fetch(url, options).then(handleResponse);
};

const get = path =>
  window.fetch(`${apiBaseUrl()}${path}`, { credentials: 'include' }).then(handleResponse);

// Orders run by our own order engine instead of Sharetribe (server/api/localOrdersApi.js).
// Responses are shaped like Marketplace API transaction responses.
export const fetchLocalCheckoutConfig = () => get('/api/orders/checkout-config');
export const speculateLocalOrder = body => post('/api/orders/speculate', body);
export const initiateLocalOrder = body => post('/api/orders', body);
export const showLocalOrder = id => get(`/api/orders/${encodeURIComponent(id)}`);
export const transitionLocalOrder = (id, body) =>
  post(`/api/orders/${encodeURIComponent(id)}/transition`, body);
export const queryLocalMessages = (id, { page, perPage }) =>
  get(`/api/orders/${encodeURIComponent(id)}/messages?page=${page}&perPage=${perPage}`);
export const sendLocalMessage = (id, content) =>
  post(`/api/orders/${encodeURIComponent(id)}/messages`, { content });
// Stock of a listing the order engine owns (404 when it does not).
export const fetchLocalStock = listingId => get(`/api/orders/stock/${encodeURIComponent(listingId)}`);
// Public reviews on local orders: { listingId } or { subjectId }.
export const queryLocalPublicReviews = params =>
  get(`/api/orders/reviews?${new URLSearchParams(params).toString()}`);
// Inbox page (orders or sales), local orders together with the server's copy of Sharetribe's.
export const queryLocalInbox = params => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  return get(`/api/orders/inbox?${search.toString()}`);
};

// Saved cards and payout accounts on our server (server/api/localPaymentsApi.js). Responses are
// shaped like the Marketplace API's stripeCustomer / stripeAccount responses.
export const fetchLocalPaymentsConfig = userId =>
  get(`/api/payments/config?userId=${encodeURIComponent(userId)}`);
export const showLocalStripeCustomer = () => get('/api/payments/customer');
export const createLocalSetupIntent = () => post('/api/payments/setup-intents', {});
export const saveLocalDefaultCard = stripePaymentMethodId =>
  post('/api/payments/customer/payment-method', { stripePaymentMethodId });
export const removeLocalDefaultCard = () => post('/api/payments/customer/payment-method/remove', {});
export const fetchLocalStripeAccount = ({ details = true } = {}) =>
  get(`/api/payments/stripe-account${details ? '' : '?details=0'}`);
export const createLocalStripeAccount = body => post('/api/payments/stripe-account', body);
export const updateLocalStripeAccount = body => post('/api/payments/stripe-account/update', body);
export const createLocalStripeAccountLink = body => post('/api/payments/stripe-account-links', body);

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const syncGuestCheckoutPromotixUser = body => {
  return post('/api/guest-checkout/sync-promotix-user', body);
};

/** SES buyer "order placed" email (unverified buyers); Flex poller is backstop. */
export const sendGuestBuyerOrderConfirmation = body => {
  return post('/api/guest-checkout/send-buyer-order-confirmation', body);
};

/** Persist guest checkout email for cart-recovery sequence (even without expire-payment). */
export const captureCartRecoveryIntent = body => {
  return post('/api/cart-recovery/capture-intent', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};
export const closeListingAfterSold = body => {
  return post('/api/close-listing', body);
};

export const createUserWithOpenIdpApi = body => {
  return post('/api/auth/create-user-with-open-idp', body);
};

export const getSellerAppUserApi = body => {
  return post('/api/auth/get-seller-app-user', body);
};
export const sellerUserExist = body => {
  return post('/api/auth/seller-user-exist', body);
};
export const loginUserWithOpenIdp = body => {
  return post('/api/auth/login-with-open-idp', body);
};
export const sendMail = body =>{
  return post('/api/send-email',body)
}

export const submitTeakOrder = body => {
  return post('/api/teak/orders', body);
};

const jsonFetch = (path, { method = 'GET', body, headers } = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  };
  return window.fetch(url, options).then(async res => {
    const data = await res.json().catch(() => ({}));
    if (res.status >= 400) {
      const e = new Error(data.error || res.statusText || 'Request failed');
      e.status = res.status;
      e.data = data;
      throw e;
    }
    return data;
  });
};

/** Customer rewards balance + ledger + claims for the logged-in Flex user. */
export const fetchRewardsBalance = () => jsonFetch('/api/rewards/balance');

export const submitRewardsClaim = body => jsonFetch('/api/rewards/claims', { method: 'POST', body });

export const adminRewardsMe = () => jsonFetch('/api/rewards/admin/me');

export const adminRewardsSearch = ({ email, userId } = {}) => {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (userId) params.set('userId', userId);
  const qs = params.toString();
  return jsonFetch(`/api/rewards/admin/search${qs ? `?${qs}` : ''}`);
};

export const adminRewardsAccount = userId =>
  jsonFetch(`/api/rewards/admin/accounts/${encodeURIComponent(userId)}`);

export const adminRewardsAdjust = (userId, body) =>
  jsonFetch(`/api/rewards/admin/accounts/${encodeURIComponent(userId)}/adjust`, {
    method: 'POST',
    body,
  });

export const adminRewardsClaims = ({ status } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const qs = params.toString();
  return jsonFetch(`/api/rewards/admin/claims${qs ? `?${qs}` : ''}`);
};

export const adminRewardsReviewClaim = (claimId, body) =>
  jsonFetch(`/api/rewards/admin/claims/${encodeURIComponent(claimId)}/review`, {
    method: 'POST',
    body,
  });


/** After decline/cancel/refund transitions; server calls TEAK Policy Void (see TEAK_INTEGRATION.md). */
export const notifyTeakPolicyCanceled = body => {
  return post('/api/teak/cancel-policy', body);
};

/** After successful POST /api/teak/orders; stores policy id on transaction metadata (Integration API). */
export const recordTeakPolicyMetadata = body => {
  return post('/api/teak/record-policy-metadata', body);
};

/** Persist GA4 client_id/session_id on transaction metadata for later Measurement Protocol purchase. */
export const recordGa4AttributionMetadata = body => {
  return post('/api/ga4/record-attribution', body);
};

/** Persist affiliate aff_* + computed commission on transaction metadata. */
export const recordAffiliateAttributionMetadata = body => {
  return post('/api/affiliate/record-attribution', body);
};